<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { RouterLink } from 'vue-router'
import { api, ApiError } from '../api'
import OrnateFrame from '../components/OrnateFrame.vue'
import { formatWeddingDate, t } from '../i18n'
import { site } from '../site'
import type { MediaType, PreparedUpload } from '../types'

type UploadStatus = 'waiting' | 'uploading' | 'processing' | 'done' | 'error'
type PendingFile = {
  clientId: string
  file: File
  url: string
  type: MediaType
  status: UploadStatus
  progress: number
  error: string
  previewFailed: boolean
}

const pending = ref<PendingFile[]>([])
const guestName = ref('')
const guestMessage = ref('')
const busy = ref(false)
const showSuccess = ref(false)
const successCount = ref(0)
const formError = ref('')
const activeRequests = new Set<XMLHttpRequest>()
const showSplash = ref(true)
let splashTimer: number | undefined
let previousBodyOverflow = ''

const hasPending = computed(() => pending.value.length > 0)
const totalProgress = computed(() => {
  const total = pending.value.reduce((sum, item) => sum + item.file.size, 0)
  if (!total) return 0
  return Math.round(pending.value.reduce((sum, item) => sum + item.file.size * item.progress, 0) / total)
})
const processing = computed(() => pending.value.some((item) => item.status === 'processing'))
const weddingDate = computed(() => formatWeddingDate(site.weddingDateIso, site.weddingDate))
const submitLabel = computed(() => {
  if (busy.value) return processing.value ? t('upload.submitProcessing') : t('upload.submitUploading', { progress: totalProgress.value })
  if (!pending.value.length) return t('upload.submitEmpty')
  return pending.value.length === 1 ? t('upload.submitOne') : t('upload.submitMany', { count: pending.value.length })
})

const mimeByExtension: Record<string, string> = {
  jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp', heic: 'image/heic', heif: 'image/heif', avif: 'image/avif',
  mp4: 'video/mp4', m4v: 'video/mp4', mov: 'video/quicktime', webm: 'video/webm',
}

function fileMime(file: File) {
  return file.type || mimeByExtension[file.name.split('.').pop()?.toLowerCase() ?? ''] || ''
}

function fileKind(file: File): MediaType | null {
  const mime = fileMime(file)
  if (mime.startsWith('image/')) return 'image'
  if (mime.startsWith('video/')) return 'video'
  return null
}

function prettySize(bytes: number) {
  if (bytes >= 1024 * 1024 * 1024) return `${Number((bytes / 1024 / 1024 / 1024).toFixed(1))} GB`
  return bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(bytes >= 10 * 1024 * 1024 ? 0 : 1)} MB` : `${Math.ceil(bytes / 1024)} KB`
}

function prettyLimit(megabytes: number) {
  return megabytes >= 1024 ? `${Number((megabytes / 1024).toFixed(1))} GB` : `${megabytes} MB`
}

function addFiles(files: File[]) {
  formError.value = ''
  const remaining = Math.max(0, site.uploadLimits.filesPerBatch - pending.value.length)
  if (files.length > remaining) formError.value = t('upload.maxFiles', { count: site.uploadLimits.filesPerBatch })
  let totalBytes = pending.value.reduce((sum, item) => sum + item.file.size, 0)
  const batchLimitBytes = site.uploadLimits.batchMb * 1024 * 1024
  for (const file of files.slice(0, remaining)) {
    const type = fileKind(file)
    if (!type) {
      formError.value = t('upload.unsupportedType', { name: file.name })
      continue
    }
    const limitMb = type === 'image' ? site.uploadLimits.imageMb : site.uploadLimits.videoMb
    const max = limitMb * 1024 * 1024
    if (file.size > max) {
      formError.value = t('upload.fileTooLarge', {
        name: file.name,
        imageLimit: prettyLimit(site.uploadLimits.imageMb),
        videoLimit: prettyLimit(site.uploadLimits.videoMb),
      })
      continue
    }
    if (totalBytes + file.size > batchLimitBytes) {
      formError.value = t('upload.batchTooLarge', { limit: prettyLimit(site.uploadLimits.batchMb) })
      continue
    }
    totalBytes += file.size
    pending.value.push({
      clientId: crypto.randomUUID(),
      file,
      url: URL.createObjectURL(file),
      type,
      status: 'waiting',
      progress: 0,
      error: '',
      previewFailed: false,
    })
  }
}

function onPick(event: Event) {
  const input = event.target as HTMLInputElement
  addFiles(Array.from(input.files ?? []))
  input.value = ''
}

function onDrop(event: DragEvent) {
  if (!busy.value) addFiles(Array.from(event.dataTransfer?.files ?? []))
}

function removeFile(clientId: string) {
  if (busy.value) return
  const index = pending.value.findIndex((item) => item.clientId === clientId)
  if (index < 0) return
  URL.revokeObjectURL(pending.value[index].url)
  pending.value.splice(index, 1)
}

function uploadBlob(
  url: string,
  body: Blob,
  onProgress: (loaded: number) => void,
  contentType?: string,
  uploadTicket?: string,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    activeRequests.add(xhr)
    xhr.open('PUT', url)
    if (contentType) xhr.setRequestHeader('Content-Type', contentType)
    if (uploadTicket) xhr.setRequestHeader('X-Upload-Ticket', uploadTicket)
    xhr.upload.onprogress = (event) => onProgress(event.loaded)
    xhr.onerror = () => reject(new Error(t('upload.connectionLost')))
    xhr.onabort = () => reject(new Error(t('upload.aborted')))
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve(xhr.getResponseHeader('ETag') ?? '')
      else reject(new Error(t('upload.storageRejected', { status: xhr.status })))
    }
    xhr.onloadend = () => activeRequests.delete(xhr)
    xhr.send(body)
  })
}

async function withRetry<T>(work: () => Promise<T>, attempts = 3): Promise<T> {
  let lastError: unknown
  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    try {
      return await work()
    } catch (error) {
      lastError = error
      if (attempt < attempts) await new Promise((resolve) => window.setTimeout(resolve, 700 * 2 ** (attempt - 1)))
    }
  }
  throw lastError
}

async function uploadOne(item: PendingFile, prepared: PreparedUpload) {
  item.status = 'uploading'
  item.progress = 0
  let completedParts: Array<{ partNumber: number; etag: string }> | undefined

  if (prepared.mode === 'single') {
    await withRetry(() => uploadBlob(
      prepared.uploadUrl,
      item.file,
      (loaded) => { item.progress = loaded / item.file.size },
      prepared.contentType,
      prepared.relay ? prepared.ticket : undefined,
    ))
  } else {
    completedParts = []
    let completedBytes = 0
    for (const part of prepared.parts) {
      const start = (part.partNumber - 1) * prepared.partSize
      const end = Math.min(start + prepared.partSize, item.file.size)
      const blob = item.file.slice(start, end, '')
      const etag = await withRetry(() => uploadBlob(part.uploadUrl, blob, (loaded) => {
        item.progress = Math.min(1, (completedBytes + loaded) / item.file.size)
      }, undefined, prepared.relay ? prepared.ticket : undefined))
      if (!etag) throw new Error(t('upload.noChecksum'))
      completedBytes += blob.size
      completedParts.push({ partNumber: part.partNumber, etag })
      item.progress = completedBytes / item.file.size
    }
  }

  item.progress = 1
  item.status = 'processing'
  await api('/api/uploads/complete', {
    method: 'POST',
    body: JSON.stringify({ ticket: prepared.ticket, parts: completedParts }),
  })
  item.status = 'done'
}

async function runWithConcurrency(items: PendingFile[], prepared: Map<string, PreparedUpload>, concurrency: number) {
  let cursor = 0
  async function worker() {
    while (cursor < items.length) {
      const item = items[cursor++]
      const target = prepared.get(item.clientId)
      if (!target) {
        item.status = 'error'
        item.error = t('upload.prepareFailed')
        continue
      }
      try {
        await uploadOne(item, target)
      } catch (error) {
        item.status = 'error'
        item.error = error instanceof Error ? error.message : t('upload.failed')
      }
    }
  }
  await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, () => worker()))
}

async function submit() {
  if (!pending.value.length || busy.value) return
  busy.value = true
  formError.value = ''
  for (const item of pending.value) {
    item.status = 'waiting'
    item.progress = 0
    item.error = ''
  }

  try {
    const response = await api<{ uploads: PreparedUpload[] }>('/api/uploads/prepare', {
      method: 'POST',
      body: JSON.stringify({
        guestName: guestName.value,
        message: guestMessage.value,
        files: pending.value.map((item) => ({
          clientId: item.clientId,
          name: item.file.name,
          size: item.file.size,
          mime: fileMime(item.file),
        })),
      }),
    })
    const attempted = [...pending.value]
    await runWithConcurrency(attempted, new Map(response.uploads.map((item) => [item.clientId, item])), 2)
    const failed = attempted.filter((item) => item.status === 'error')
    const completed = attempted.filter((item) => item.status === 'done')

    if (failed.length) {
      for (const item of completed) URL.revokeObjectURL(item.url)
      pending.value = failed
      formError.value = completed.length
        ? t('upload.partialFailure', { completed: completed.length, failed: failed.length })
        : t('upload.completeFailure')
    } else {
      successCount.value = completed.length
      for (const item of completed) URL.revokeObjectURL(item.url)
      pending.value = []
      guestName.value = ''
      guestMessage.value = ''
      showSuccess.value = true
    }
  } catch (error) {
    formError.value = error instanceof ApiError || error instanceof Error ? error.message : t('upload.startFailure')
  } finally {
    busy.value = false
  }
}

function resetForm() {
  showSuccess.value = false
  formError.value = ''
}

function hideSplash() {
  showSplash.value = false
}

function releaseSplashScrollLock() {
  document.body.style.overflow = previousBodyOverflow
}

onMounted(() => {
  previousBodyOverflow = document.body.style.overflow
  document.body.style.overflow = 'hidden'
  splashTimer = window.setTimeout(hideSplash, 2400)
})

onBeforeUnmount(() => {
  if (splashTimer) window.clearTimeout(splashTimer)
  releaseSplashScrollLock()
  for (const request of activeRequests) request.abort()
  for (const item of pending.value) URL.revokeObjectURL(item.url)
})
</script>

<template>
  <div class="upload-page">
    <Transition name="splash" @after-leave="releaseSplashScrollLock">
      <OrnateFrame v-if="showSplash" left-rose="/assets/rose-pink.png" right-rose="/assets/rose-red.png" class="upload-hero">
        <div class="upload-hero__inner">
          <div class="eyebrow">{{ t('upload.heroEyebrow') }}</div>
          <h1 class="script upload-hero__names">{{ site.coupleNames }}</h1>
          <img src="/assets/flourish.svg" alt="" class="flourish" />
          <div class="upload-hero__date">{{ weddingDate }}</div>
        </div>
      </OrnateFrame>
    </Transition>

    <div id="upload-area" class="upload-content">
      <header class="upload-intro">
        <img src="/assets/flourish.svg" alt="" class="flourish upload-intro__flourish" />
        <h2>{{ t('upload.introTitle') }}</h2>
        <p>{{ t('upload.introText') }}</p>
      </header>

      <OrnateFrame v-if="showSuccess" class="success-card" aria-live="polite">
        <img src="/assets/rose-branch.png" alt="" class="success-card__rose" />
        <h2 class="script">{{ t('upload.successTitle') }}</h2>
        <p>{{ successCount === 1 ? t('upload.successOne') : t('upload.successMany', { count: successCount }) }}</p>
        <button class="button-primary" type="button" @click="resetForm">{{ t('upload.more') }}</button>
      </OrnateFrame>

      <form v-else class="upload-form" @submit.prevent="submit">
        <label
          class="file-picker"
          :class="{ 'file-picker--disabled': busy }"
          for="file-input"
          @dragover.prevent
          @drop.prevent="onDrop"
        >
          <img src="/assets/camera.svg" alt="" />
          <strong>{{ t('upload.pickerTitle') }}</strong>
          <span>{{ t('upload.pickerHint') }}</span>
          <img src="/assets/flourish.svg" alt="" class="flourish flourish--small" />
        </label>
        <input id="file-input" class="screen-reader-only" type="file" accept="image/*,video/*,.heic,.heif" multiple :disabled="busy" @change="onPick" />

        <div v-if="hasPending" class="preview-grid" :aria-label="t('upload.selectedFiles')">
          <article v-for="item in pending" :key="item.clientId" class="preview-card" :class="`preview-card--${item.status}`">
            <img
              v-if="item.type === 'image' && !item.previewFailed"
              :src="item.url"
              :alt="item.file.name"
              @error="item.previewFailed = true"
            />
            <video v-else-if="item.type === 'video'" :src="item.url" muted playsinline preload="metadata" />
            <div v-else class="preview-card__fallback">HEIC</div>
            <span v-if="item.type === 'video'" class="preview-card__type">VIDEO</span>
            <button v-if="!busy" type="button" class="preview-card__remove" :aria-label="t('upload.removeFile', { name: item.file.name })" @click="removeFile(item.clientId)">×</button>
            <div v-if="item.status === 'uploading' || item.status === 'processing'" class="preview-card__progress">
              <span :style="{ width: `${Math.round(item.progress * 100)}%` }" />
            </div>
            <div v-if="item.status === 'processing'" class="preview-card__veil">{{ t('upload.processing') }}</div>
            <div v-if="item.status === 'error'" class="preview-card__veil preview-card__veil--error">{{ t('upload.retry') }}</div>
            <small class="preview-card__size">{{ prettySize(item.file.size) }}</small>
          </article>
        </div>

        <div class="upload-fields">
          <div v-if="formError" class="error-banner" role="alert">{{ formError }}</div>
          <input v-model="guestName" class="field" maxlength="120" autocomplete="name" :placeholder="t('upload.namePlaceholder')" :disabled="busy" />
          <textarea v-model="guestMessage" class="field" maxlength="2000" rows="3" :placeholder="t('upload.messagePlaceholder')" :disabled="busy" />
          <div v-if="busy" class="overall-progress" aria-live="polite">
            <div><span>{{ processing ? t('upload.safeProcessing') : t('upload.keepOpen') }}</span><b>{{ totalProgress }} %</b></div>
            <i><span :style="{ width: `${totalProgress}%` }" /></i>
          </div>
          <button type="submit" class="button-primary" :disabled="!hasPending || busy">{{ submitLabel }}</button>
        </div>
      </form>

      <footer class="upload-footer">
        <img src="/assets/flourish.svg" alt="" class="flourish" />
        <p>{{ t('upload.coupleOnly') }} <RouterLink to="/album">{{ t('upload.toAlbum') }}</RouterLink></p>
      </footer>
    </div>
  </div>
</template>

<style scoped>
.upload-page { width: 100%; padding-bottom: 48px; }
.upload-hero { position: fixed; inset: 0; z-index: 40; display: flex; width: 100%; min-height: 100vh; min-height: 100svh; min-height: 100dvh; align-items: center; justify-content: center; padding: 64px 24px; border-right: 0; border-left: 0; background: radial-gradient(circle at 50% 42%, #fffdf7 0, #fbf7ee 42%, #f4ead6 100%); text-align: center; }
.upload-hero :deep(.ornate-frame__content) { display: flex; width: 100%; min-height: calc(100vh - 128px); min-height: calc(100dvh - 128px); align-items: center; justify-content: center; }
.upload-hero :deep(.ornate-frame__rose) { width: clamp(230px, 42vw, 520px); opacity: .92; }
.upload-hero :deep(.ornate-frame__rose--left) { top: clamp(-130px, -10vw, -60px); left: clamp(-145px, -9vw, -65px); }
.upload-hero :deep(.ornate-frame__rose--right) { right: clamp(-150px, -10vw, -66px); bottom: clamp(-135px, -10vw, -62px); }
.upload-hero__inner { position: relative; z-index: 1; width: min(100%, 720px); }
.upload-hero__names { margin: 12px 0 8px; color: var(--ink); font-size: clamp(58px, 11vw, 112px); line-height: 1.05; font-weight: 400; }
.upload-hero__date { margin-top: 12px; color: var(--muted); font-size: clamp(17px, 2vw, 23px); font-style: italic; letter-spacing: .14em; }
.upload-content { width: 100%; max-width: 460px; margin: 0 auto; padding: 1px 20px 0; scroll-margin-top: 12px; }
.upload-intro { margin: 52px 0 22px; text-align: center; }
.upload-intro__flourish { margin-bottom: 12px; }
.upload-intro h2 { margin: 0; font-size: 30px; font-weight: 500; line-height: 1.25; text-wrap: pretty; }
.upload-intro p { margin: 8px 0 0; color: var(--muted); font-size: 17px; font-style: italic; line-height: 1.5; }
.success-card { padding: 38px 22px; text-align: center; animation: fade-up .4s ease both; }
.success-card__rose { width: 120px; margin-bottom: -4px; mix-blend-mode: multiply; -webkit-mask-image: linear-gradient(#000 72%, transparent 98%); mask-image: linear-gradient(#000 72%, transparent 98%); }
.success-card h2 { margin: 0; color: var(--gold); font-size: 40px; font-weight: 400; }
.success-card p { margin: 6px 0 0; color: var(--muted); font-size: 18px; font-style: italic; line-height: 1.5; }
.success-card button { margin-top: 22px; }
.file-picker { display: block; padding: 34px 20px; border: 1px dashed #b39a67; background: var(--paper); text-align: center; cursor: pointer; transition: background .2s; }
.file-picker:hover { background: #f8f1e2; }
.file-picker--disabled { cursor: wait; opacity: .7; }
.file-picker > img:first-child { display: block; width: 122px; margin: 0 auto; }
.file-picker strong { display: block; margin-top: 10px; font-size: 20px; font-weight: 600; }
.file-picker > span { display: block; margin-top: 4px; color: #8a7c60; font-size: 15px; font-style: italic; }
.file-picker .flourish { margin-top: 12px; }
.preview-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin-top: 14px; }
.preview-card { position: relative; aspect-ratio: 1; overflow: hidden; border: 1px solid #d9c8a3; background: #e9e0cc; }
.preview-card > img, .preview-card > video { display: block; width: 100%; height: 100%; object-fit: cover; }
.preview-card__fallback { display: grid; width: 100%; height: 100%; place-items: center; color: var(--gold-dark); font-size: 14px; letter-spacing: .12em; }
.preview-card__type { position: absolute; bottom: 6px; left: 6px; padding: 2px 7px; background: rgba(58, 50, 38, .75); color: var(--cream); font-size: 10px; letter-spacing: .1em; }
.preview-card__remove { position: absolute; top: 4px; right: 4px; display: grid; width: 28px; height: 28px; padding: 0 0 2px; place-items: center; border: 0; border-radius: 50%; background: rgba(58, 50, 38, .82); color: var(--cream); font: 19px/1 sans-serif; cursor: pointer; }
.preview-card__size { position: absolute; right: 5px; bottom: 5px; padding: 1px 4px; background: rgba(251, 247, 238, .86); color: var(--muted); font-size: 10px; }
.preview-card__progress { position: absolute; right: 0; bottom: 0; left: 0; height: 4px; background: rgba(251, 247, 238, .75); }
.preview-card__progress span { display: block; height: 100%; background: var(--gold); transition: width .15s linear; }
.preview-card__veil { position: absolute; inset: auto 0 0; padding: 5px 2px 7px; background: rgba(58, 50, 38, .78); color: var(--cream); font-size: 11px; text-align: center; letter-spacing: .04em; }
.preview-card__veil--error { inset: 0; display: grid; place-items: center; padding: 5px; background: rgba(111, 49, 31, .82); }
.preview-card--error { border-color: #96543a; }
.upload-fields { display: flex; flex-direction: column; gap: 12px; margin-top: 22px; }
.upload-fields textarea { resize: vertical; line-height: 1.5; }
.overall-progress div { display: flex; justify-content: space-between; gap: 12px; color: var(--muted); font-size: 14px; font-style: italic; }
.overall-progress b { color: var(--gold-dark); font-style: normal; font-weight: 600; white-space: nowrap; }
.overall-progress > i { display: block; height: 5px; margin-top: 5px; overflow: hidden; background: #ddd1b8; }
.overall-progress > i span { display: block; height: 100%; background: var(--gold); transition: width .2s linear; }
.upload-footer { margin-top: 44px; color: var(--gold); text-align: center; }
.upload-footer p { margin: 10px 0 0; color: var(--faded); font-size: 14px; font-style: italic; }
.upload-footer a { text-decoration: underline; text-underline-offset: 2px; }
.splash-leave-active { transition: opacity .65s ease, transform .65s ease, filter .65s ease; }
.splash-leave-to { opacity: 0; filter: blur(3px); transform: scale(1.025); }
@media (max-width: 520px) {
  .upload-hero { padding: 52px 18px; }
  .upload-hero :deep(.ornate-frame__content) { min-height: calc(100vh - 104px); min-height: calc(100dvh - 104px); }
  .upload-hero :deep(.ornate-frame__rose) { width: min(72vw, 300px); }
  .upload-hero :deep(.ornate-frame__rose--right) { right: -105px; bottom: -95px; width: min(64vw, 270px); }
  .upload-hero__names { font-size: clamp(54px, 18vw, 78px); }
}
@media (max-width: 360px) { .upload-content { padding-inline: 12px; } .upload-intro h2 { font-size: 27px; } }
</style>
